import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import * as adminHandlers from '../../../../routes/Admin/redux/handlers';

import ImageUploadButton from '../../../Shared/ImageUploadButton';
import StatusToggle from '../../../Shared/StatusToggle';
import { DrawerIcon } from '../../../../assets/icons/DrawerIcon';
import { BrandInfo } from '../../../../assets/icons/BrandInfo';
import { AdditionalInfoIcon } from '../../../../assets/icons/AdditionalInfoIcon';
import { EditIcon } from '../../../../assets/icons/EditIcon';
import { SaveIcon } from '../../../../assets/icons/SaveIcon';


// Validation Schema
const categoryValidationSchema = Yup.object().shape({
    name: Yup.string()
        .required('Category name is required')
        .min(2, 'Category name must be at least 2 characters')
        .max(100, 'Category name must be less than 100 characters'),
    sequence: Yup.number()
        .required('Sequence is required')
        .positive('Sequence must be a positive number')
        .integer('Sequence must be an integer'),
    is_active: Yup.boolean()
});


const CategoryViewModal = ({ category, isOpen, onClose, onUpdate }) => {
    const dispatch = useDispatch();
    const { categories, loading } = useSelector(state => state.admin);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedCategory, setEditedCategory] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    
    const currentCategory = categories.find(c => c.id === category?.id) || category;
    
    useEffect(() => {
        if (isOpen && currentCategory?.id) {
            dispatch(adminHandlers.fetchCategoriesHandler());
            setEditedCategory({
                ...currentCategory
            });
            setIsEditMode(false);
            setValidationErrors({});
        }
    }, [isOpen, currentCategory?.id, dispatch]);

    const handleEditClick = () => {
        setIsEditMode(true);
        setValidationErrors({});
        setEditedCategory({
            ...currentCategory
        });
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditedCategory({
            ...currentCategory
        });
        setValidationErrors({});
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            const dataToSend = {
                name: editedCategory.name,
                sequence: editedCategory.sequence || 1,
                is_active: editedCategory.is_active,
                description: editedCategory.description || ''
            };

            // Only send image when it's a freshly picked base64 data URL (not the
            // existing S3 URL the backend already stored).
            if (editedCategory.image && editedCategory.image.startsWith("data:")) {
                dataToSend.image = editedCategory.image;
            }

            // Validate the form
            await categoryValidationSchema.validate(editedCategory, { abortEarly: false });
                        
            // If validation passes, save the changes
            const updatedCategory = await dispatch(adminHandlers.updateCategoryHandler(editedCategory.id, dataToSend));
            
            // Re-fetch the categories list to ensure Redux store is fully updated
            await dispatch(adminHandlers.fetchCategoriesHandler({ page: 1, size: 100 }));
            
            // Update editedCategory with the fresh data from server
            if (updatedCategory) {
                setEditedCategory({
                    ...updatedCategory
                });
            }
            
            setIsEditMode(false);
            setValidationErrors({});
            setIsSaving(false);
            
            if (onUpdate && updatedCategory) {
                onUpdate(updatedCategory);
            }
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const errors = {};
                error.inner.forEach((err) => {
                    errors[err.path] = err.message;
                });
                setValidationErrors(errors);
            }
            setIsSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setEditedCategory(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear validation error for this field when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    if (!currentCategory) return null;

    return (
        <div className={`fixed inset-0 z-50 overflow-hidden ${isOpen ? '' : 'pointer-events-none'}`}>
            {/* Overlay */}
            <div 
                className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${
                    isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
                }`}
                onClick={onClose}
            />
            
            {/* Sidebar Panel */}
            <div className={`fixed right-0 top-0 h-full w-[580px] bg-eerie-black shadow-xl flex flex-col transform transition-all duration-300 ease-in-out ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                {/* Header */}
                <div className="flex flex-col">
                    <div className='border-b border-zinc-700'>
                        <button
                            onClick={onClose}
                            className="mx-4 my-3 bg-charleston-green rounded-lg transition-colors"
                            >
                            <DrawerIcon width={38} height={34} />
                        </button>
                    </div>

                    <div className="flex flex-col px-4 py-3">
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl text-white">Category</h2>
                            {!isEditMode ? (
                                <div className="flex space-x-2">
                                    <button 
                                        onClick={handleEditClick}
                                        className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 flex items-center justify-center space-x-2"
                                        >
                                        <EditIcon width={20} height={20} />
                                        <span className="text-sm">Edit Category</span>
                                    </button>
                                    <span></span>
                                </div>
                            ) : (
                                <div className="flex space-x-2">
                                    {!isSaving && (
                                        <button 
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 flex" 
                                        >
                                            <span className="text-sm">Cancel</span>
                                        </button>
                                    )}
                                    <button 
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className={`px-4 py-2 text-white border rounded-full flex items-center justify-center space-x-2 ${
                                            isSaving 
                                                ? "bg-gray-600 border-gray-600 cursor-not-allowed" 
                                                : "bg-green-600 border-green-600 hover:bg-green-500"
                                        }`}
                                    >
                                        {isSaving ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="text-sm">Saving...</span>
                                            </>
                                        ) : (
                                            <>
                                                <SaveIcon width={20} height={20} />
                                                <span className="text-sm">Save Changes</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditMode && Object.keys(validationErrors).length > 0 && (
                            <div className="mt-2 p-2 bg-red-950 border border-red-800 rounded-lg">
                                <p className="text-red-400 text-xs">Please fix the validation errors before saving.</p>
                            </div>
                        )}
                    </div>

                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="px-4 pb-4 pt-2 space-y-6">
                        {/* Category Information Section */}
                        <div className="">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="bg-charleston-green rounded-lg flex items-center justify-center">
                                    <BrandInfo width={32} height={32} />
                                </div>
                                <h3 className="text-white font-medium">Category Information</h3>
                            </div>

                            <div className="space-y-4">
                                {/* Category Logo */}
                                <div>
                                    <p className="text-md mb-2">Category Logo</p>
                                    <div className="flex items-center space-x-4">
                                        <div className="w-20 h-20 bg-white p-0.5 inline-block rounded-lg">
                                            <img
                                                src={(isEditMode && editedCategory?.image) || currentCategory.image}
                                                alt={currentCategory.name}
                                                className="w-full h-full object-contain rounded-lg"
                                            />
                                        </div>
                                        {isEditMode && (
                                            <ImageUploadButton
                                                onPick={(dataUrl) => handleInputChange("image", dataUrl)}
                                                onReset={() => handleInputChange("image", currentCategory.image)}
                                                canReset={!!editedCategory?.image && editedCategory.image !== currentCategory.image}
                                                label="Change Logo"
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Category Name */}
                                <div>
                                    <p className="text-md mb-1">Category Name</p>
                                    <input
                                        type="text"
                                        value={isEditMode ? editedCategory?.name : currentCategory.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        readOnly={!isEditMode}
                                        className={`w-full bg-charleston-green border rounded-lg px-3 py-2 text-white text-sm ${
                                            validationErrors.name ? 'border-red-500' : 'border-zinc-700'
                                        } ${
                                            isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                        }`}
                                    />
                                    {validationErrors.name && (
                                        <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div>
                                    <p className="text-md mb-1">Description</p>
                                    <textarea
                                        value={isEditMode ? (editedCategory?.description || '') : (currentCategory.description || '')}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        readOnly={!isEditMode}
                                        rows={3}
                                        className={`w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm ${
                                            isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                        }`}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Additional Information Section */}
                        <div className="">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="bg-charleston-green rounded-lg flex items-center justify-center">
                                    <AdditionalInfoIcon width={32} height={32} />
                                </div>
                                <h3 className="text-white font-medium">Additional Information</h3>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <p className="text-md mb-1">Sequence</p>
                                    <input
                                        type="number"
                                        value={isEditMode ? (editedCategory?.sequence || 1) : (currentCategory.sequence || 1)}
                                        onChange={(e) => handleInputChange('sequence', parseInt(e.target.value))}
                                        readOnly={!isEditMode}
                                        className={`w-full bg-charleston-green border rounded-lg px-3 py-2 text-white text-sm ${
                                            validationErrors.sequence ? 'border-red-500' : 'border-zinc-700'
                                        } ${
                                            isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                        }`}
                                    />
                                    {validationErrors.sequence && (
                                        <p className="text-red-500 text-xs mt-1">{validationErrors.sequence}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="text-md mb-2">Status</p>
                                    {!isEditMode ? (
                                        <span className={`inline-block px-4 py-1.5 text-xs rounded-full ${
                                            currentCategory.is_active
                                                ? 'bg-green-950 text-green-500 border border-green-500'
                                                : 'bg-red-950 text-red-500 border border-red-500'
                                        }`}>
                                            {currentCategory.is_active ? 'ACTIVE' : 'INACTIVE'}
                                        </span>
                                    ) : (
                                        <StatusToggle
                                            checked={!!editedCategory?.is_active}
                                            onChange={(v) => handleInputChange('is_active', v)}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryViewModal;